import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import PageShell from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { CompassMark } from "@/components/CompassMark";
import { CONTACT_EMAIL } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENTS = [
  { value: "coaching", label: "Coaching" },
  { value: "speaking", label: "Speaking" },
  { value: "collaboration", label: "Collaboration" },
  { value: "media", label: "Media / Interview" },
  { value: "professional", label: "Professional Opportunity" },
  { value: "general", label: "General Correspondence" },
];

const COACHING_FOCUS = ["Personal", "Business", "Marketing", "Sales", "Not sure yet"];

export default function Contact() {
  const [params] = useSearchParams();
  const initialIntent = params.get("intent");
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    intent: INTENTS.find((i) => i.value === initialIntent) ? initialIntent : "general",
    message: "",
  });
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialIntent && INTENTS.find((i) => i.value === initialIntent)) {
      setForm((f) => ({ ...f, intent: initialIntent }));
    }
  }, [initialIntent]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateDetail = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Name, email, and message are required.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (form.intent === "coaching" || form.intent === "speaking") {
        const cleaned = Object.fromEntries(
          Object.entries(details).filter(([, v]) => v && String(v).trim())
        );
        if (Object.keys(cleaned).length) payload.details = cleaned;
      }
      const res = await axios.post(`${API}/contact`, payload);
      setDone(true);
      toast.success("Received. Thank you.", { description: `Reference ID: ${res.data.id.slice(0, 8)}` });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again, or write to me directly.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      testId="page-contact"
      title="Contact | Quentin Young"
      description="Contact Quentin Young for professional opportunities, collaboration, media, speaking, coaching, partnerships, or thoughtful correspondence."
      ogImage="/og-contact.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Contact", url: "/contact" }]}
    >
      <Toaster theme="dark" position="bottom-right" toastOptions={{ className: "font-mono text-xs" }} />

      <section className="container-edge pt-16 md:pt-24 pb-12">
        <p className="eyebrow-gold mb-5">Contact</p>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance leading-[1.02]">
          One channel. Read carefully.
        </h1>
        <p className="mt-8 max-w-2xl font-serif text-lg md:text-xl text-paper-secondary leading-relaxed text-pretty">
          Coaching, speaking, collaboration, media, a professional opportunity, or a thoughtful letter, write directly. Brevity is welcome.
        </p>
      </section>

      <section className="border-t border-ink-line bg-ink-base py-16">
        <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">What this is for</p>
            <ul className="space-y-3 text-paper-secondary text-base leading-relaxed">
              <li>· Coaching and speaking inquiries with substance.</li>
              <li>· Professional opportunities and partnerships.</li>
              <li>· Press, podcast, and editorial inquiries.</li>
              <li>· Speaking engagements with substance.</li>
              <li>· Thoughtful correspondence from intelligent strangers.</li>
            </ul>

            <p className="eyebrow mt-12 mb-5">What it is not for</p>
            <ul className="space-y-3 text-paper-tertiary text-sm leading-relaxed">
              <li>· Cold sales.</li>
              <li>· SEO outreach.</li>
              <li>· Mass mail.</li>
            </ul>

            <p className="eyebrow mt-12 mb-4">Before you write</p>
            <div className="flex flex-col gap-3">
              <Link to="/coaching" data-testid="contact-learn-coaching" className="btn-arrow">
                <span>Learn about coaching</span>
                <span className="arrow">→</span>
              </Link>
              <Link to="/speaking" data-testid="contact-learn-speaking" className="btn-arrow">
                <span>Learn about speaking</span>
                <span className="arrow">→</span>
              </Link>
            </div>

            <p className="eyebrow mt-12 mb-3">Or write directly</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-mailto"
              className="font-serif text-xl text-paper-primary hover:text-gold-warm transition-colors duration-300 underline underline-offset-4 decoration-paper-mute hover:decoration-gold"
            >
              {CONTACT_EMAIL}
            </a>

            <div className="mt-16 relative">
              <CompassMark size={120} opacity={0.15} />
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="contact-success" className="border border-gold/40 bg-ink-surface p-10 md:p-12">
                <p className="eyebrow-gold mb-4">Received.</p>
                <h2 className="display-serif text-3xl md:text-4xl text-paper-primary">Thank you for writing.</h2>
                <p className="mt-5 text-paper-secondary leading-relaxed max-w-md">
                  Serious correspondence is read carefully. You will hear back when there is something worth saying.
                </p>
                <button
                  type="button"
                  data-testid="contact-reset"
                  onClick={() => {
                    setDone(false);
                    setForm({ name: "", email: "", organization: "", intent: "general", message: "" });
                    setDetails({});
                  }}
                  className="btn-arrow mt-10"
                >
                  <span>Send another</span>
                  <span className="arrow">→</span>
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8" noValidate>
                <div>
                  <label className="field-label" htmlFor="cf-name">Name</label>
                  <input id="cf-name" data-testid="contact-name" required value={form.name} onChange={update("name")} type="text" autoComplete="name" className="field-input" />
                </div>
                <div>
                  <label className="field-label" htmlFor="cf-email">Email</label>
                  <input id="cf-email" data-testid="contact-email" required type="email" autoComplete="email" value={form.email} onChange={update("email")} className="field-input" />
                </div>
                <div>
                  <label className="field-label" htmlFor="cf-org">Organization (optional)</label>
                  <input id="cf-org" data-testid="contact-org" type="text" autoComplete="organization" value={form.organization} onChange={update("organization")} className="field-input" />
                </div>
                <div>
                  <label className="field-label" htmlFor="cf-intent">Intent</label>
                  <select
                    id="cf-intent"
                    data-testid="contact-intent"
                    value={form.intent}
                    onChange={update("intent")}
                    className="field-input bg-ink-base cursor-pointer"
                  >
                    {INTENTS.map((i) => (
                      <option key={i.value} value={i.value} className="bg-ink-base text-paper-primary">{i.label}</option>
                    ))}
                  </select>
                </div>

                {form.intent === "coaching" && (
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 border-t border-ink-line pt-8" data-testid="coaching-fields">
                    <p className="md:col-span-2 eyebrow-gold">Coaching details (optional)</p>
                    <div>
                      <label className="field-label" htmlFor="cf-focus">Area of focus</label>
                      <select id="cf-focus" data-testid="contact-coaching-focus" value={details.focus || ""} onChange={updateDetail("focus")} className="field-input bg-ink-base cursor-pointer">
                        <option value="" className="bg-ink-base text-paper-mute">Select one</option>
                        {COACHING_FOCUS.map((f) => (
                          <option key={f} value={f} className="bg-ink-base text-paper-primary">{f}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="cf-format">Preferred format</label>
                      <input id="cf-format" data-testid="contact-coaching-format" type="text" value={details.format || ""} onChange={updateDetail("format")} className="field-input" placeholder="In person, virtual, either" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="field-label" htmlFor="cf-timeline">Timeline</label>
                      <input id="cf-timeline" data-testid="contact-coaching-timeline" type="text" value={details.timeline || ""} onChange={updateDetail("timeline")} className="field-input" placeholder="Immediate, next month, exploring" />
                    </div>
                  </div>
                )}

                {form.intent === "speaking" && (
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 border-t border-ink-line pt-8" data-testid="speaking-fields">
                    <p className="md:col-span-2 eyebrow-gold">Engagement details (optional)</p>
                    <div>
                      <label className="field-label" htmlFor="cf-eventtype">Event type</label>
                      <input id="cf-eventtype" data-testid="contact-speaking-eventtype" type="text" value={details.eventType || ""} onChange={updateDetail("eventType")} className="field-input" placeholder="Keynote, panel, workshop, fireside" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="cf-eventdate">Event date</label>
                      <input id="cf-eventdate" data-testid="contact-speaking-date" type="text" value={details.eventDate || ""} onChange={updateDetail("eventDate")} className="field-input" placeholder="Approximate is fine" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="cf-audience">Audience</label>
                      <input id="cf-audience" data-testid="contact-speaking-audience" type="text" value={details.audience || ""} onChange={updateDetail("audience")} className="field-input" placeholder="Who is in the room" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="cf-location">Location or virtual</label>
                      <input id="cf-location" data-testid="contact-speaking-location" type="text" value={details.location || ""} onChange={updateDetail("location")} className="field-input" placeholder="City, or virtual" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="field-label" htmlFor="cf-topic">Topic of interest</label>
                      <input id="cf-topic" data-testid="contact-speaking-topic" type="text" value={details.topic || ""} onChange={updateDetail("topic")} className="field-input" placeholder="A theme, or what you want the room to see differently" />
                    </div>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="field-label" htmlFor="cf-message">Message</label>
                  <textarea id="cf-message" data-testid="contact-message" required rows={6} value={form.message} onChange={update("message")} className="field-textarea" placeholder="Briefly. What is this about?" />
                </div>

                {error && (
                  <p data-testid="contact-error" className="md:col-span-2 font-mono text-xs uppercase tracking-[0.18em] text-red-300">
                    {error}
                  </p>
                )}

                <div className="md:col-span-2 flex items-center justify-between gap-6 pt-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute max-w-sm">
                    No newsletter. No marketing list. Stored for the purpose of reply.
                  </p>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="btn-quiet disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <span>{loading ? "Sending…" : "Send"}</span>
                    <span>→</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="While you wait for a reply."
        rooms={[
          { label: "Explore the thinking", to: "/thinking", hint: "how Quentin sees it" },
          { label: "See what is being built", to: "/building", hint: "the work" },
          { label: "Return to the beginning", to: "/", hint: "the whole" },
        ]}
        testId="contact-next"
      />
    </PageShell>
  );
}
